import { useEffect, useMemo } from "react";
import { Stack, Typography, ButtonGroup, Button } from "@mui/material";
import { camelCase, get } from "lodash";
import { setBranch, setOrdersFlow, setOrdersSource } from "@/features/order/slice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { Order } from "@/types";
import { Branch } from "@/types/branch";
import { DINING_MODES, MENU_FLOWS, ORDER_SOURCE_OPTIONS, ORDER_SOURCES } from "@/constants/global";
import { checkServiceAvailability } from "@/utils/weekDays";
import ServicePicker from "@/components/ServicePicker";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import { skipToken } from "@reduxjs/toolkit/query";

const AdminOrderSettings = () => {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const activeBranchId = useAppSelector((store) => store.ui.activeBranch);
  const organization = useAppSelector((store) => store.organizations[organizationId]);

  const ordersSource = useAppSelector((state) => state.order.source);
  const orderFlow = useAppSelector((state) => state.order.flow);
  const branch = useAppSelector((state) => state.order.branch);

  const organizationBranch = useFetchBranchQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });
  const branches = organizationBranch?.data && Array.isArray(organizationBranch.data) 
    ? organizationBranch.data 
    : organization?.branches || [];

  const dineModes = useMemo(() => {
    const branchToUse = branches.find((b: Branch) => b.id === (branch?.id || activeBranchId));

    return DINING_MODES.filter((mode) => {
      if (branches.length > 0 && branchToUse) {
        const hasService = branchToUse.services?.includes(mode.id);
        const isBranchOpen = checkServiceAvailability({
          isEnabled: true,
          schedule: branchToUse.schedule,
        });
        return hasService && isBranchOpen;
      }

      const serviceConfig = get(organization, `settings.services.${camelCase(mode.id)}`);
      return checkServiceAvailability(serviceConfig);
    });
  }, [organization, branches, branch?.id, activeBranchId]);

  useEffect(() => {
    if (!ordersSource) {
      dispatch(setOrdersSource(ORDER_SOURCES.CALL));
    }

    if (!orderFlow && dineModes.length > 0) {
      const deliveryMode = dineModes.find((m) => m.id === MENU_FLOWS.DELIVERY);
      dispatch(setOrdersFlow(deliveryMode?.id ?? dineModes[0].id));
    }

    let selectedBranch = null;
    const activeBranch = branches.find((b: Branch) => b.id === activeBranchId);
    const mainBranch = branches.find((b: Branch) => b.isMainBranch);

    if (activeBranchId) {
      selectedBranch = activeBranch;
    } else if (mainBranch) {
      selectedBranch = mainBranch;
    } else if (branches.length === 1) {
      selectedBranch = branches[0];
    }

    dispatch(
      setBranch({
        id: selectedBranch?.id,
        title: selectedBranch?.title,
        location: selectedBranch?.location,
        whatsapp: selectedBranch?.whatsapp,
      })
    );
  }, [
    activeBranchId,
    branches.length
  ]);

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>

        <Stack spacing={1} flex={1}>
          <Typography variant="body2">¿Por dónde nos contactó?</Typography>
          <ButtonGroup size="small" color="primary" fullWidth>
            {ORDER_SOURCE_OPTIONS.map((src) => (
              <Button
                key={src.value}
                variant={
                  (ordersSource ?? ORDER_SOURCES.WHATSAPP) === src.value
                    ? "contained"
                    : "outlined"
                }
                onClick={() =>
                  dispatch(setOrdersSource(src.value as Order["source"]))
                }
              >
                {src.label}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>
        {!activeBranchId && branches.length > 0 && (
          <Stack spacing={1} flex={1}>
            <Typography variant="body2">¿En qué sucursal se procesará?</Typography>
            <ButtonGroup size="small" color="primary" fullWidth>
              {branches.map((b: Branch) => (
                <Button
                  key={b.id}
                  variant={
                    (branch?.id ?? "") === b.id ? "contained" : "outlined"
                  }
                  onClick={() => {
                    dispatch(
                      setBranch({
                        id: b.id,
                        title: b.title,
                        location: b.location,
                        whatsapp: b.whatsapp,
                      })
                    );
                  }}
                >
                  {b.title}
                </Button>
              ))}
            </ButtonGroup>
          </Stack>
        )}
      </Stack>

      <Stack spacing={1}>
        <Typography variant="body2">¿Cómo se entregará el pedido?</Typography>
        <ServicePicker
          value={orderFlow}
          onChange={(val) => dispatch(setOrdersFlow(val))}
        />
      </Stack>
    </Stack>
  );
};

export default AdminOrderSettings;
